/**
 * ConversionCTA - Consistent bottom conversion block for all marketing pages
 * Headline + 2 bullets + primary CTA + secondary link
 */

import { Button } from '@/components/ui/button';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { redirectToStripeCheckout } from '@/lib/stripe';
import { Link } from 'react-router-dom';
import { ScrollReveal } from '@/components/landing/ScrollReveal';
import { Section } from './Section';
import { Container } from './Container';

interface ConversionCTAProps {
  headline?: string;
  bullets?: [string, string];
  primaryCTA?: string;
  secondaryLink?: {
    text: string;
    href: string;
  };
  variant?: 'default' | 'secondary';
}

export function ConversionCTA({
  headline = 'Ready to remove bad decisions?',
  bullets = [
    'Join professionals who value efficiency',
    'Protect revenue with automated prioritization',
  ],
  primaryCTA = 'Start Free Trial',
  secondaryLink = {
    text: 'See pricing',
    href: '/pricing',
  },
  variant = 'secondary',
}: ConversionCTAProps) {
  return (
    <Section variant={variant} spacing="lg">
      <Container size="lg" center>
        <ScrollReveal>
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-[var(--font-h2)] font-semibold tracking-[var(--tracking-tighter)] mb-6 leading-[var(--leading-headline)]">
              {headline}
            </h2>
            
            {/* Bullets */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              {bullets.map((bullet, index) => (
                <div key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                  <span>{bullet}</span>
                </div>
              ))}
            </div>

            {/* Primary CTA */}
            <Button 
              variant="default"
              size="lg" 
              onClick={redirectToStripeCheckout}
              className="px-8 mb-4"
            >
              {primaryCTA}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>

            {/* Secondary Link */}
            {secondaryLink && (
              <p className="text-sm text-muted-foreground">
                <Link 
                  to={secondaryLink.href}
                  className="hover:text-foreground transition-colors underline underline-offset-4"
                >
                  {secondaryLink.text}
                </Link>
                {' • '}
                <span>Cancel anytime. Setup in minutes.</span>
              </p>
            )}
          </div>
        </ScrollReveal>
      </Container>
    </Section>
  );
}

